import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { DurationAnalysisDetail, TriggerDurationRequest } from './types';

const durationKey = (date: string | undefined) =>
  ['alm', 'duration', 'latest', date] as const;

/**
 * GET /api/v1/alm/duration?date=YYYY-MM-DD
 *
 * Returns the persisted Duration result for the given tenant + date, or null
 * if no calculation has run for that date yet.
 *
 * When `date` is undefined the server returns the most recent result.
 */
export function useLatestDuration(
  date: string | undefined,
  options?: Omit<
    UseQueryOptions<DurationAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: durationKey(date),
    enabled: Boolean(date),
    queryFn: async () => {
      const url = `${apiPath('alm', 'duration')}?date=${encodeURIComponent(date!)}`;
      const data = await apiGet<DurationAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/duration/calculate — triggers an on-demand Duration calculation.
 * Server returns 202 Accepted with the DurationAnalysisDetail.
 *
 * Duration has no horizonMonths — all future cashflows are included.
 */
export function useTriggerDuration(
  options?: UseMutationOptions<
    DurationAnalysisDetail,
    CubixApiError,
    TriggerDurationRequest
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<DurationAnalysisDetail, TriggerDurationRequest>(
        apiPath('alm', 'duration', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['alm', 'duration'] });
      return options?.onSuccess?.(...args);
    },
  });
}
